import React, { useState, useEffect } from 'react'
import { useUser } from 'util/UserContext'
import Vote from './Vote'
import Save from './Save'
import EntitySpecificActions from './EntitySpecificActions'
import './EntityActions.css'
import { Issue, Impact, Solution, EntityType, Comment, Feedback, Influence, Action, Collection } from 'util/constants'
import { MessageCircle } from 'lucide-react'
import ShareActions from 'components/Common/ShareActions'
import ContentAwardButton from 'components/Common/ContentAwardButton'
import { useCommentsDrawer } from 'components/Comments/CommentsDrawerProvider'
import api from 'config/axiosConfig'
import { trackEntityAction } from 'util/analytics'
import { apiRoutes } from "util/apiRoutes";
import { buildEntitySharePath, buildShareRouteUrl } from "util/shareUrls";

interface Props {
    entity: Issue | Solution | Impact | Comment | Feedback | Influence | Action | Collection
    entityType: EntityType | any
    onVote?: () => void
    onSave?: () => void
    showEntitySpecificActions?: boolean
    showSave?: boolean
    showShare?: boolean
    showVote?: boolean
    showComment?: boolean
    /**
     * Lay actions out as a vertical Instagram-reels-style action bar with
     * stacked icon+count items. Used by `MediaViewer` to overlay actions
     * along the right edge of the media without obscuring video controls.
     */
    vertical?: boolean
}

const EntityActions = ({
    entity,
    entityType,
    onVote,
    onSave,
    showEntitySpecificActions = true,
    showSave = true,
    showShare = true,
    showVote = true,
    showComment = true,
    vertical = false,
}: Props) => {
    const { currentUser } = useUser();
    const commentsDrawer = useCommentsDrawer();
    // The card layer passes the canonical `entityType` (e.g. `'teabate'`
    // for teabate posts; `entity_type = posts.kind` from the entity view).
    // Share, award, and story surfaces all key off this directly.
    const storyEntityType = String(entityType);
    const awardEntityType = storyEntityType;
    const awardTargetLabel =
        "title" in entity && typeof entity.title === "string" && entity.title.trim().length > 0
            ? entity.title
            : "name" in entity && typeof entity.name === "string" && entity.name.trim().length > 0
              ? entity.name
              : awardEntityType;
    // Awardable entity kinds. Teabates are first-class (`posts.kind='teabate'`,
    // surfaced as `entity_type='teabate'` by the entity view).
    const showContentAward = ['impact', 'teabate', 'solution', 'action', 'comment', 'feedback', 'collection'].includes(
        String(entityType)
    );
    const [commentCount, setCommentCount] = useState<number>(0)
    const entityId = entityType === 'issue'
        ? (entity as Issue).cname
        : (entity as Solution | Impact | Comment | Feedback | Influence | Action | Collection).id
    // Canonical in-app path (e.g. "/impact/123"). Used for internal navigation
    // like the comment button. Must not include the "/s/" share prefix, since
    // "/s/*" is a share-preview route that redirects externally rather than
    // rendering the actual profile + comments experience.
    const canonicalPath = buildEntitySharePath(String(entityType), entityId)
    // External share URL (e.g. "https://example.com/s/impact/123"). Only used
    // for outbound sharing via ShareActions.
    const shareUrl = buildShareRouteUrl(canonicalPath)
    const commentsPath = `${canonicalPath}/comments`
    
    // Fetch comment count when component mounts or entity changes
    useEffect(() => {
        const fetchCommentCount = async () => {
            if (entityType !== 'comment') {
                try {
                    const response = await api.get(apiRoutes.comments.countByCommentable(entityType, entity.id))
                    if (response.data && response.data.count !== undefined) {
                        setCommentCount(response.data.count)
                    }
                } catch (error) {
                    console.error('Error fetching comment count:', error)
                }
            } else {
                // If this is a comment, fetch reply count
                try {
                    const response = await api.get(apiRoutes.comments.repliesCountByCommentId(entity.id))
                    if (response.data && response.data.count !== undefined) {
                        setCommentCount(response.data.count)
                    }
                } catch (error) {
                    console.error('Error fetching reply count:', error)
                }
            }
        }
        
        fetchCommentCount()
    }, [entityType, entityId, entity])

    const handleComment = () => {
        // Track comment button click
        trackEntityAction.comment(entityType, entityId, commentCount)

        // Preferred path: open the Instagram-style Next comments drawer in
        // place. Works from feed cards, grids, profile action rows — any
        // place `EntityActions` renders. Falls through to the legacy page
        // navigation when the `comments_v2_drawer` flag is off or when
        // the entity type isn't wired into the drawer contract (e.g.
        // `user` profiles).
        if (
            commentsDrawer.isAvailable &&
            entityType !== 'user' &&
            entityType !== 'team' &&
            entityType !== 'comment'
        ) {
            commentsDrawer.open({
                entityType: entityType as Comment["commentable_type"],
                entityId: entity.id,
                onMutation: () => {
                    setCommentCount((prev) => (typeof prev === 'number' ? prev + 1 : prev))
                },
            })
            return
        }

        if (window.location.pathname === commentsPath) {
            const commentsSection = document.getElementsByClassName('comment-title')[0]
            if (commentsSection) {
                commentsSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest',
                })
            }
        } else {
            window.location.href = commentsPath
        }
    }

    if (vertical) {
        // Instagram-reels-style vertical action rail. Each item stacks its
        // icon over a small count label. The Vote/Award components handle
        // their own vertical layout via their `vertical` prop.
        return (
            <div className="entity-actions ignore-card-click flex flex-col items-center gap-3">
                {showVote && (
                    <Vote
                        entityId={entity.id}
                        onVote={() => {
                            trackEntityAction.vote(entityType, entityId);
                            onVote?.();
                        }}
                        voteCount={entity.vote_count || 0}
                        entityType={entityType}
                        vertical
                    />
                )}
                {showContentAward ? (
                    <ContentAwardButton
                        entityType={awardEntityType}
                        entityId={entity.id}
                        authorUserId={entity.user_id}
                        targetLabel={awardTargetLabel}
                        vertical
                    />
                ) : null}
                {showComment && (
                    <button
                        type="button"
                        className="action comment flex flex-col items-center gap-0.5 text-eoe-gray dark:text-eoe-dark-icon-stroke hover:bg-white/15 rounded-full p-1 transition-colors"
                        onClick={handleComment}
                        title={commentCount > 0 ? `${commentCount} comment${commentCount !== 1 ? "s" : ""}` : "No comments yet"}
                        aria-label="Comment"
                    >
                        <MessageCircle className="size-7" />
                        <span className="text-xs font-semibold leading-none">
                            {commentCount}
                        </span>
                    </button>
                )}
                {/* Entity-specific actions (e.g., participants for actions) */}
                {showEntitySpecificActions && entityType !== "comment" && (
                    <EntitySpecificActions
                        entity={entity}
                        entityType={entityType}
                        actionLabel={false}
                        vertical
                    />
                )}
                {showShare && (
                    <ShareActions
                        url={shareUrl}
                        title={`Check out this ${entityType} on Societea`}
                        text={`I found this interesting ${entityType} on Societea!`}
                        sourceEntityType={entityType}
                        storyEntityType={storyEntityType}
                        sourceEntityId={entity.id}
                        onShare={(method: string) => {
                            trackEntityAction.share(entityType, entityId, method);
                        }}
                    />
                )}
                {showSave && (
                    <div className="action save flex relative">
                        <Save
                            entityId={entity.id}
                            entityType={entityType}
                            currentUser={currentUser}
                            onSave={(action?: "save" | "unsave") => {
                                if (action) {
                                    trackEntityAction.save(entityType, entityId, action);
                                }
                                onSave?.();
                            }}
                        />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={`flex-wrap ${entityType !== 'comment' ? 'entity-actions flex items-center gap-x-1' : 'ignore-card-click entity-actions flex items-center md:flex-row'}`}>
            {showVote && (
                <Vote
                entityId={entity.id}
                onVote={() => {
                    // Track vote action
                    trackEntityAction.vote(entityType, entityId)
                    onVote?.()
                }}
                voteCount={entity.vote_count || 0}
                entityType={entityType}
            />
            )}
            {showContentAward ? (
                <ContentAwardButton
                    entityType={awardEntityType}
                    entityId={entity.id}
                    authorUserId={entity.user_id}
                    targetLabel={awardTargetLabel}
                    compact={entityType === 'comment'}
                />
            ) : null}
            <div className="action comment flex text-xl">
                {entityType !== 'comment' && showComment && (
                <button
                    className="h-7 rounded-full gap-x-1 px-1.5 text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors"
                    onClick={handleComment}
                    title={commentCount > 0 ? `${commentCount} comment${commentCount !== 1 ? 's' : ''}` : 'No comments yet'}
                >
                    <MessageCircle className="size-5"/>
                    {commentCount > 0 && (
                        <span className="text-sm font-semibold">{commentCount}</span>
                    )}
                </button>
                )}
            </div>
            
            {/* Entity-specific actions */}
            {showEntitySpecificActions && entityType !== 'comment' && (
                <EntitySpecificActions entity={entity} entityType={entityType} actionLabel={showVote === false} />
            )}
            
            <div className="flex-grow"></div>

            {entityType !== 'comment' && showShare && (
                <>
                    <ShareActions 
                        url={shareUrl}
                        title={`Check out this ${entityType} on Societea`}
                        text={`I found this interesting ${entityType} on Societea!`}
                        sourceEntityType={entityType}
                        storyEntityType={storyEntityType}
                        sourceEntityId={entity.id}
                        onShare={(method: string) => {
                            // Track share action
                            trackEntityAction.share(entityType, entityId, method)
                        }}
                    />
                </>
            )}
            {entityType !== 'comment' && showSave && (
                <>
                    <div className="action save flex text-xl relative">
                        <Save
                            entityId={entity.id}
                            entityType={entityType}
                            currentUser={currentUser}
                            onSave={(action?: 'save' | 'unsave') => {
                                // Track save action
                                if (action) {
                                    trackEntityAction.save(entityType, entityId, action)
                                }
                                onSave?.()
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

export default EntityActions
